/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useCallback } from '@web-stories-wp/react';

/**
 * Internal dependencies
 */
import elementIs from '../elements/utils/elementIs';
import { useStory } from '../app/story';
import { getMediaBaseColor } from './getMediaBaseColor';

function getBackgroundElement(elements = []) {
  return elements.find(
    (element) => elementIs.backgroundable(element) && element.isBackground
  );
}

function useSetMediaAsBackground() {
  const {
    combineElements,
    currentPage,
    setBackgroundElement,
    updateElementsById,
  } = useStory(
    ({
      actions: { combineElements, setBackgroundElement, updateElementsById },
      state: { currentPage },
    }) => ({
      combineElements,
      currentPage,
      setBackgroundElement,
      updateElementsById,
    })
  );

  return useCallback(
    (element) => {
      if (!element?.resource) {
        return;
      }

      const currentBackgroundId = getBackgroundElement(
        currentPage?.elements
      )?.id;

      if (currentBackgroundId === element.id) {
        return;
      }

      const setBackground = (baseColor) => {
        const nextElement = baseColor
          ? {
              ...element,
              resource: {
                ...element.resource,
                baseColor,
              },
            }
          : element;

        if (currentBackgroundId) {
          combineElements({
            firstElement: nextElement,
            secondId: currentBackgroundId,
          });
          return;
        }

        if (baseColor) {
          updateElementsById({
            elementIds: [element.id],
            properties: {
              resource: nextElement.resource,
            },
          });
        }
        setBackgroundElement({ elementId: element.id });
      };

      if (element.resource.baseColor) {
        setBackground();
      } else {
        getMediaBaseColor(element.resource, setBackground);
      }
    },
    [
      combineElements,
      currentPage?.elements,
      setBackgroundElement,
      updateElementsById,
    ]
  );
}

export default useSetMediaAsBackground;
